import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Slide,
  useMediaQuery,
  useScrollTrigger,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  DashboardRounded,
  HomeRounded,
  LocalShippingRounded,
  PersonRounded,
  ReceiptLongRounded,
  RestaurantMenuRounded,
  ShoppingCartRounded,
  StorefrontRounded,
  AssessmentRounded,
} from '@mui/icons-material';
import { getCurrentUser } from '../services/authService';
import { useI18n } from '../i18n/I18nProvider';

const getNavItems = (role, t) => {
  if (role === 'admin') {
    return [
      { label: t('nav_home', 'Home'), value: '/', icon: <HomeRounded /> },
      { label: t('nav_admin', 'Admin'), value: '/admin', icon: <DashboardRounded /> },
      { label: t('nav_profile', 'Profile'), value: '/profile', icon: <PersonRounded /> },
    ];
  }

  if (role === 'restaurant') {
    return [
      { label: t('nav_home', 'Home'), value: '/', icon: <HomeRounded /> },
      { label: t('nav_restaurant', 'Restaurant'), value: '/restaurant', icon: <StorefrontRounded /> },
      { label: t('nav_reports', 'Reports'), value: '/restaurant/reports', icon: <AssessmentRounded /> },
      { label: t('nav_profile', 'Profile'), value: '/profile', icon: <PersonRounded /> },
    ];
  }

  if (role === 'restaurant_supervisor') {
    return [
      { label: t('nav_home', 'Home'), value: '/', icon: <HomeRounded /> },
      { label: t('nav_reports', 'Reports'), value: '/restaurant/reports', icon: <AssessmentRounded /> },
      { label: t('nav_profile', 'Profile'), value: '/profile', icon: <PersonRounded /> },
    ];
  }

  if (role === 'driver') {
    return [
      { label: t('nav_home', 'Home'), value: '/', icon: <HomeRounded /> },
      { label: t('nav_driver', 'Driver'), value: '/driver', icon: <LocalShippingRounded /> },
      { label: t('nav_profile', 'Profile'), value: '/profile', icon: <PersonRounded /> },
    ];
  }

  return [
    { label: t('nav_home', 'Home'), value: '/', icon: <HomeRounded /> },
    { label: t('nav_browse', 'Browse'), value: '/restaurants', icon: <RestaurantMenuRounded /> },
    { label: t('nav_orders', 'Orders'), value: '/orders', icon: <ReceiptLongRounded /> },
    { label: t('nav_cart', 'Cart'), value: '/cart', icon: <ShoppingCartRounded /> },
    { label: t('nav_profile', 'Profile'), value: '/profile', icon: <PersonRounded /> },
  ];
};

const MobileNav = () => {
  const { t } = useI18n();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const hide = useScrollTrigger({ threshold: 20 });
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = React.useState(getCurrentUser);
  const items = React.useMemo(() => getNavItems(user?.role, t), [user?.role, t]);

  React.useEffect(() => {
    const sync = () => setUser(getCurrentUser());
    window.addEventListener('auth-changed', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('auth-changed', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  if (!isMobile) return null;

  const current = [...items]
    .sort((a, b) => b.value.length - a.value.length)
    .find((item) => item.value !== '/' && location.pathname.startsWith(item.value)) ||
    items.find((item) => item.value === '/');
  const value = current ? current.value : '/';

  return (
    <Slide appear direction="up" in={!hide}>
      <Paper
        elevation={12}
        sx={(theme) => ({
          position: 'fixed',
          left: 12,
          right: 12,
          bottom: 12,
          borderRadius: 999,
          overflow: 'hidden',
          border:
            theme.palette.mode === 'dark'
              ? '1px solid rgba(148, 163, 184, 0.18)'
              : '1px solid rgba(15, 23, 42, 0.08)',
          zIndex: 1200,
          backdropFilter: 'blur(18px)',
          transition: 'transform 220ms ease',
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 18px 48px rgba(0, 0, 0, 0.35)'
              : '0 18px 48px rgba(15, 23, 42, 0.18)',
        })}
      >
        <BottomNavigation
          value={value}
          onChange={(_, newValue) => navigate(newValue)}
          showLabels
          sx={(theme) => ({
            bgcolor:
              theme.palette.mode === 'dark'
                ? 'rgba(15, 23, 42, 0.92)'
                : 'rgba(255, 255, 255, 0.9)',
            px: 0.5,
            '& .MuiBottomNavigationAction-root': {
              minWidth: 0,
              px: 0.8,
              py: 0.9,
              borderRadius: 999,
              mx: 0.25,
              transition: 'all 180ms ease',
            },
            '& .MuiBottomNavigationAction-root.Mui-selected': {
              color: 'primary.main',
              transform: 'translateY(-1px)',
              bgcolor:
                theme.palette.mode === 'dark'
                  ? 'rgba(15, 118, 110, 0.18)'
                  : 'rgba(15, 118, 110, 0.1)',
            },
          })}
        >
          {items.map((item) => (
            <BottomNavigationAction
              key={item.value}
              label={item.label}
              value={item.value}
              icon={item.icon}
              aria-label={item.label}
            />
          ))}
        </BottomNavigation>
      </Paper>
    </Slide>
  );
};

export default MobileNav;
